import IconButton from "components/blocks/IconButton";
import GridView from "components/GridView";
import { GridSchema, GridViewHeaderProps } from "components/GridView/GridView.types";
import { useCookieContext } from "contexts";
import { useBreadcrumb } from "contexts/breadcrumb/BreadcrumbContext";
import useGridData from "hooks/useGridData";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import {useEffect, useMemo, useState} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AuthorPaymentResult } from "services/authorsPaymentsService/models/authorsPaymentsTypes";
import {RewardResponse} from "../../../../services/RewardsService/models";
import RewardConfirmationModal from "../../../../components/RewardsModals/RewardConfirmationModal";
import { useTitle } from "hooks/useTitle";

type RewardConfirmationModalProps = {
  isOpen: boolean;
  data? : RewardResponse | undefined,
  name: string;
};
const AuthorsPaymentsGridView = () => {
  const { t } = useTranslation("Authors");
  useTitle(t("authorsPayments"));
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumb();
  const { toSystemThemeDateFormat } = useSystemDateTheme();
  const { isAr } = useCookieContext();
  const [rewardConfirmationModalState, setRewardConfirmationModalState] = useState<RewardConfirmationModalProps>({
    isOpen: false,
    name: "",
  });

  const { loading, gridData, totalCount,fetchGridData, exportToExcel } = useGridData<"rewardsService", AuthorPaymentResult>({
    service: "rewardsService",
    endpoint: "getAllAuthorsRewards",
    exportExcelEndpoint: "exportAuthorsRewardsToExcel",
    exportFileName: "authorsPayments",
    localizationNameSpace: "Authors",
  });

  useEffect(() => {
    setBreadcrumbs([
      {
        localizationKey: "authorsPayments",
        path: "/home/authorsManagement/authorsPayments",
        menuItemId: "authors",
      },
    ]);
  }, []);

  const onConfirmationModalOpen = (row: RewardResponse) => {
    setRewardConfirmationModalState({
      isOpen: true,
      data: row,
      name: (isAr ? row.user?.arabicName : row.user?.englishName) ?? "",
    });
  };
  const gridSchema = useMemo<GridSchema[]>(
    () => [
      {
        field: "id",
        displayName: t("authorName"),
        sortable: true,
        filterable: true,
        showOnMobile: true,
        render: (row: RewardResponse) => {
          return isAr ? row?.user?.arabicName ?? "" : row?.user?.englishName ??"";
        },
      },
      {
        field: "orderNumber",
        displayName: t("orderNumber"),
        render: (row: RewardResponse) => {
          return (
            <button className="btn btn-link" onClick={() => navigate(`/home/orders/${row.courseOrderId}`)}>
              {row?.courseOrder?.serialNumber}
            </button>
          );
        },
      },
      {
        field: "paymentDate",
        displayName: t("paymentDate"),
        render: (row: RewardResponse) => {
          return toSystemThemeDateFormat(row.approvalDate);
        },
      },
      {
        field: "paymentAmount",
        displayName: t("paymentAmount"),
        render: (row: RewardResponse) => {
          return <>{row?.value} {t("Common:riyal")}</>;
        },
      },
      {
        field: "status",
        displayName: t("Common:status"),
        sortable: true,
        filterable: true,
        showOnMobile: true,
        render(row: RewardResponse) {
          return (
            <span className={row?.rewardStatus?.styleCode}>
              {row.rewardStatus?.details?.[0]?.displayName}
            </span>
          );
        },
      },
      {
        field: "fileId",
        displayName: t("paymentReceipt"),
        showOnMobile: true,
        render: (row: RewardResponse) => {
          return (
              <div  className={"d-flex gap-2"}>
                 <IconButton
                    title={t("Common:edit")}
                    icon="icon-edit"
                    fitToIconWidth
                    innerIconColor="dark"
                    size="md"
                    onClick={() => onConfirmationModalOpen(row)}
                />

                {row?.fileId &&  <IconButton
                    title={t("paymentReceipt")}
                    icon="icon-receipt"
                    fitToIconWidth
                    innerIconColor="dark"
                    size="md"
                    onClick={() => {
                      const downloadUrl = `/api/FileManager/DownloadFile/${row.fileId}`;
                      let anchor = document.createElement("a");
                      anchor.href = downloadUrl;
                      anchor.target = "_blank";
                      anchor.download = row.file?.name?? t("paymentReceipt")
                      anchor.click();

                    }}
                />}
              </div>
          );
        },
      },
    ],
    [t, toSystemThemeDateFormat],
  );

  const viewHeader: GridViewHeaderProps = {
    title: t("authorsPayments"),
    singularName: t("Common:result"),
    totalCount: totalCount,
    onExportClick: exportToExcel,
  };
  const onConfirmationModalClose = (refetch = false) => {
    if (refetch) {
      fetchGridData();
    }
    setRewardConfirmationModalState({
      isOpen: false,
      name: ""
    })
  }
  return (
      <>
    <GridView
      loading={loading}
      gridProps={{
        data: gridData ?? [],
        gridSchema,
      }}
      viewHeaderProps={viewHeader}
    />
  <RewardConfirmationModal
      type="author"
      isOpen={rewardConfirmationModalState.isOpen}
      onClose={onConfirmationModalClose}
      data={rewardConfirmationModalState.data}
  />
  </>
  );
};

export default AuthorsPaymentsGridView;
